import React, { FC } from 'react'
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles'
import { Card, Grid } from '@material-ui/core'

import { StylesProps } from '../../../types/interface/styles.interface'
import { Typography } from '@dg-shared'
import { TestPropsType, TranslatedTextType } from '../../../types'
import { ThemeBrandPageBackgroundNameType } from 'BrandTheme/types'
import { PartnerName } from '../../../types/interface/quote.interface'
import ProductWrapper from '../ProductWrapper'

export const useLDCardTwoColStyles = makeStyles(
  (theme: Theme) =>
    createStyles({
      container: (props?: StylesProps) => ({
        alignContent: 'center',
        backgroundColor: theme.brand.colors.background,
        height: '100vh',
        justifyContent: 'center',
        overflowY: 'auto',
        [theme.breakpoints.up('md')]: {
          minWidth: '750px',
          paddingLeft: theme.spacing(3),
          paddingRight: theme.spacing(3),
          ...theme.brand.assets.pageBackgrounds[props.brandPageBackground],
        },
      }),
      card: (props?: StylesProps) => ({
        display: 'flex',
        width: '100%', // 780 | 900
        height: `${props.cardHeight}px`, // 616 | 620
        [theme.breakpoints.up('md')]: {
          maxWidth: '920px',
        },
      }),
      cardColLeft: (props?: StylesProps) => ({
        flex: '1 50%',
        display: 'flex',
        ...theme.brand.assets.pageBackgrounds[props.brandPageBackgroundCard],
        width: '100%',
        padding: `0 ${theme.spacing(4)}px`,
        justifyContent: 'center',
      }),
      cardColRight: {
        flex: '1 50%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        padding: theme.spacing(2),
        overflowY: 'auto',
        [theme.breakpoints.up('sm')]: {
          paddingTop: theme.spacing(5),
          paddingBottom: theme.spacing(5),
          paddingLeft: theme.spacing(8),
          paddingRight: theme.spacing(8),
        },
      },
      logoWrapper: {
        display: 'flex',
        width: '100%',
        position: 'relative',
        ...theme.brand.assets.layout?.logoWrapper,
      },
      logoImage: {
        alignSelf: 'center',
        maxHeight: '69px',
        margin: 'auto',
        maxWidth: '100%',
        ...theme.brand.assets.images.logoMax,
      },
      textFooter: {
        textAlign: 'center',
        padding: 0,
        marginTop: theme.spacing(1),
        marginBottom: 0,
      },
    }),
  // https://stackoverflow.com/questions/59559632/material-ui-have-different-style-result-in-production-mode
  { index: 1 }
)

export interface LDCardTwoColProps {
  textLogoAlt: TranslatedTextType
  textFooter?: TranslatedTextType
  brandPageBackground: ThemeBrandPageBackgroundNameType
  brandPageBackgroundCard?: ThemeBrandPageBackgroundNameType
  cardHeight?: number
  imageLogo: string
  isReverse?: boolean
  cardClassName?: string
  testProps?: TestPropsType
}

const LDCardTwoCol: FC<LDCardTwoColProps> = ({
  textLogoAlt,
  textFooter,
  imageLogo,
  brandPageBackground,
  brandPageBackgroundCard,
  isReverse,
  cardHeight = 620,
  children,
  cardClassName,
  testProps,
}) => {
  const classes = useLDCardTwoColStyles({
    brandPageBackgroundCard,
    brandPageBackground,
    leftColGradientReverse: isReverse,
    cardHeight,
  })

  const cardClassNameString = [classes.card, cardClassName].filter((i) => i).join(' ')

  return (
    <Grid container direction='row' component='article' className={classes.container}>
      <Card className={cardClassNameString}>
        <section data-testid='desktop-card-left' {...testProps} className={classes.cardColLeft}>
          <div id={`page-card-left`} className={classes.logoWrapper}>
            <ProductWrapper products={[]} partners={[PartnerName.skoda]} excludeMode={true}>
              <img
                id={`desctop-card-img`}
                className={classes.logoImage}
                src={imageLogo}
                alt={`${textLogoAlt} Logo`}
              />
            </ProductWrapper>
          </div>
        </section>
        <section className={classes.cardColRight}>
          {children}
          {!!textFooter && (
            <Typography size='small' color='textDisclaimer' className={classes.textFooter}>
              {textFooter}
            </Typography>
          )}
        </section>
      </Card>
    </Grid>
  )
}

export default LDCardTwoCol
